/**
 * Clamp a wall-clock-derived seek target (seconds into a camera's media
 * timeline) to a position the element can actually seek to.
 *
 * A resume point saved on an earlier visit can fall OUTSIDE the media that
 * is currently available: a live rolling window may have evicted it off the
 * back (target < buffer start), or a stale/extreme UTC can land past the
 * end. A bare `Math.max(0, target)` puts those cases at buffer start
 * (currentTime 0 — the OLDEST buffered segment, not near live) or lets the
 * seek run past the end. This clamps to the real `seekable` range (falling
 * back to `[0, duration]`).
 *
 * `intent` distinguishes a restored position from a target the user just
 * picked. `resume` snaps anything outside the live window to the live edge —
 * right for a saved position that rolled off the back of a live buffer, wrong
 * for a click on the scrubber: that must land where the user clicked, even
 * if it's behind the current window, or scrubbing backward on a live stream
 * would be impossible. `explicit` only clamps toward the live edge when the
 * target is genuinely past it.
 */

/**
 * Structural shape of the bits of `HTMLVideoElement` we read, so this stays
 * unit-testable without a DOM. `HTMLVideoElement` is assignable to it.
 */
export interface SeekableMedia {
  duration: number;
  seekable: { length: number; start(index: number): number; end(index: number): number };
}

/** Stay this far behind the live edge so the player keeps a forward buffer.
 *  Used for an ARBITRARY seek that happens to land near the edge (scrubber,
 *  lap jump, resume) — the deliberate "go live" target below uses its own,
 *  larger, segment-aware backoff instead. */
export const LIVE_EDGE_BACKOFF_S = 3;

/** A UTC-ms span — the same shape `availability.ts`'s `TimeRange` is, kept
 *  structural here so this module stays independent of that one. */
export type UtcRange = [number, number];

/** Segments to stay behind the live edge when deliberately opening AT it
 *  ("Go live", or the automatic live placement) — shared with hls.js's own
 *  `liveSyncDurationCount` config so there is exactly one place that says
 *  how many segments. Two, not one: a segment fetch has been measured taking
 *  seconds at the tail, and one segment of headroom leaves nothing to absorb
 *  that before the buffer empties. */
export const LIVE_EDGE_SYNC_SEGMENTS = 2;

/** Segment-duration fallback when the session hasn't reported its own yet
 *  (hls.js parses this off the live playlist's EXT-X-TARGETDURATION) —
 *  matches the phone encoder's default cut cadence. */
export const DEFAULT_SEGMENT_DURATION_S = 6;

/**
 * The region a deliberate live-edge seek should land in: the latest
 * contiguous PUBLISHED audio span, or the video span when audio has none —
 * audio is preferred because it is the sound/clock owner. `null` when
 * neither medium has published anything yet.
 */
export function pickLiveEdgeRegion(
  audioRegion: UtcRange | null,
  videoRegion: UtcRange | null,
): UtcRange | null {
  return audioRegion ?? videoRegion;
}

/**
 * UTC ms to open (or jump) to at the live edge: the end of `region`, backed
 * off `LIVE_EDGE_SYNC_SEGMENTS` segment durations and clamped to never fall
 * before the region's own start — so the target always lands inside
 * published media, never on an unpublished last fragment.
 */
export function liveEdgeUtcMsFromRegion(
  region: UtcRange,
  segmentDurationS: number = DEFAULT_SEGMENT_DURATION_S,
): number {
  const [start, end] = region;
  const backoffMs = segmentDurationS * LIVE_EDGE_SYNC_SEGMENTS * 1000;
  return Math.max(start, end - backoffMs);
}

/**
 * A target strictly between two playable ranges sits in a HOLE — an
 * interior gap the backend deliberately marked absent (unclaimed live
 * cell, dropped upload). Nothing will ever load there, regardless of
 * intent, so it snaps forward to the start of the next playable range
 * rather than stranding playback on an empty buffer.
 */
function snapOutOfHole(targetSec: number, ranges: Array<[number, number]> | undefined): number {
  if (!ranges || ranges.length < 2 || !Number.isFinite(targetSec)) return targetSec;
  for (let i = 0; i < ranges.length - 1; i += 1) {
    const end = ranges[i][1];
    const nextStart = ranges[i + 1][0];
    if (targetSec > end && targetSec < nextStart) return nextStart;
  }
  return targetSec;
}

export function clampSeekSeconds(
  video: SeekableMedia,
  targetSecRaw: number,
  opts: {
    live: boolean;
    intent: 'explicit' | 'resume';
    /**
     * Playable media (coverage's `playable_union`) as media-timeline
     * seconds, sorted ascending. Unlike `seekable`, this excludes trailing
     * EXT-X-GAP cells — real EXTINF duration, no bytes — so the live edge
     * doesn't land somewhere nothing will ever load. Interior entries (more
     * than one range) also mark out HOLES between them — see
     * `snapOutOfHole`. Omit to fall back to `seekable` alone.
     */
    playableRanges?: Array<[number, number]>;
  },
): number {
  const targetSec = snapOutOfHole(targetSecRaw, opts.playableRanges);
  let lo = 0;
  let hi = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : Infinity;
  const sk = video.seekable;
  if (sk && sk.length > 0) {
    lo = sk.start(0);
    hi = sk.end(sk.length - 1);
  }

  if (opts.live && Number.isFinite(hi)) {
    const lastPlayable = opts.playableRanges?.length
      ? opts.playableRanges[opts.playableRanges.length - 1]
      : null;
    const edgeHi = lastPlayable ? Math.min(hi, lastPlayable[1]) : hi;
    const edgeLo = lastPlayable ? Math.max(lo, lastPlayable[0]) : lo;
    const liveEdge = Math.max(edgeLo, edgeHi - LIVE_EDGE_BACKOFF_S);
    if (!Number.isFinite(targetSec)) return liveEdge;
    if (opts.intent === 'resume') {
      // Anywhere OUTSIDE the window — rolled off the back, or at/past the
      // edge — resumes near the live edge rather than at the oldest
      // buffered segment. An in-window target is honored as-is.
      if (targetSec < lo || targetSec > liveEdge) return liveEdge;
      return targetSec;
    }
    return Math.min(Math.max(targetSec, lo), liveEdge);
  }

  // VOD (or no known upper bound yet): clamp into [lo, hi].
  if (!Number.isFinite(targetSec)) return lo;
  if (!Number.isFinite(hi)) return Math.max(lo, targetSec);
  return Math.min(Math.max(targetSec, lo), hi);
}
